import React from 'react';

/**
 * Detects chunk/module load errors that occur after deployment
 * when old chunk files no longer exist on the server.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes('dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Importing a module script failed')
  );
}

const RETRY_DELAY_MS = 1000;

const CHUNK_RELOAD_KEY_PREFIX = 'chunk-reload-';

/**
 * Derives a stable key from the import function's toString().
 * Extracts the module path from import() and strips content hashes
 * so the key remains stable across deploys (e.g. "Foo-abc123.js" → "Foo").
 * Falls back to a hash of the full string if extraction fails.
 */
function getModuleKey(importFn: () => Promise<unknown>): string {
  const fnStr = importFn.toString();

  // Try to extract the import path: import("./pages/Foo") or import("/assets/Foo-abc123.js")
  const match = fnStr.match(/import\s*\(\s*["'](.+?)["']\s*\)/);
  if (match) {
    const importPath = match[1];
    // Get basename: "./pages/Foo" → "Foo", "/assets/Foo-abc123.js" → "Foo-abc123.js"
    const basename = importPath.split('/').pop() || importPath;
    // Strip content hash and extension: "Foo-abc123.js" → "Foo", "Foo.js" → "Foo"
    const stable = basename.replace(/[-_.][a-f0-9]{7,}(\.\w+)?$/, '').replace(/\.\w+$/, '');
    if (stable) {
      return CHUNK_RELOAD_KEY_PREFIX + stable;
    }
  }

  // Fallback: hash of full string
  let hash = 0;
  for (let i = 0; i < fnStr.length; i++) {
    hash = ((hash << 5) - hash + fnStr.charCodeAt(i)) | 0;
  }
  return CHUNK_RELOAD_KEY_PREFIX + (hash >>> 0).toString(16);
}

/**
 * Attempts a dynamic import with one retry and an optional page reload.
 * Exported for testing — consumers should use lazyWithRetry().
 */
export function importWithRetry<T>(
  importFn: () => Promise<{ default: T }>
): Promise<{ default: T }> {
  const moduleKey = getModuleKey(importFn);

  return importFn().catch((error: unknown) => {
    if (!isChunkLoadError(error)) {
      throw error;
    }

    // Retry once after delay
    return new Promise<{ default: T }>((resolve, reject) => {
      setTimeout(() => {
        importFn()
          .then(resolve)
          .catch((retryError: unknown) => {
            if (!isChunkLoadError(retryError)) {
              reject(retryError);
              return;
            }

            // Check sessionStorage to prevent infinite reload loop (per module)
            try {
              if (sessionStorage.getItem(moduleKey)) {
                // Already reloaded once this session for this module — don't loop
                reject(retryError);
                return;
              }
              sessionStorage.setItem(moduleKey, '1');
            } catch {
              // sessionStorage unavailable (e.g. private browsing) — skip reload
              reject(retryError);
              return;
            }

            window.location.reload();
            // Return a never-resolving promise to keep Suspense fallback showing
            // until navigation completes (avoids flash of error UI).
            // If reload is intercepted (e.g. service worker), page stays on loading state.
            return;
          });
      }, RETRY_DELAY_MS);
    });
  });
}

/**
 * Wraps React.lazy() with retry + reload logic for stale chunk errors.
 *
 * 1. Try the import
 * 2. On chunk load failure, retry once after a short delay
 * 3. If retry fails, reload the page (once per session per module)
 * 4. If already reloaded for this module, let the error propagate
 *    so ChunkErrorBoundary can show fallback UI
 */
// `ComponentType<any>` (not `<unknown>`) so components that take props can be
// lazy-loaded too — `unknown` props are not assignable to a concrete props type,
// which made the constraint fail for anything but prop-less page components.
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() => importWithRetry(importFn));
}
